'use client';

import { useState } from 'react';

import {
	CheckboxAlign,
	CheckboxVerticalAlign,
	FontSizes,
	FontWeights,
	Loader,
	Text,
} from '@calm-web/design-system';

import AppleAuthButton from '@/components/authForm/AppleAuthButton';
import AuthTerms from '@/components/authForm/AuthTermsRefresh';
import EmailAuthButton from '@/components/authForm/EmailAuthButton';
import FacebookAuthButton from '@/components/authForm/FacebookAuthButton';
import GoogleAuthButton from '@/components/authForm/GoogleAuthButton';
import ModeToggle from '@/components/authForm/ModeToggleRefresh';
import PartnerSpecificAuthTerms from '@/components/SimplifiedSignup/Components/PartnershipSpecificAuthTerms';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import { FormStateContextProvider } from '@/context/FormStateContextProvider';
import { usePartnerState, useRecaptchaVisibleState } from '@/hooks/store';

import { FootingWrapper, HeadingWrapper, SocialSignupWrapper, StyledMarketingOptIn, Wrapper } from './styles';
import { AuthFormProps } from './types';

const AuthForm = ({
	heading,
	subheading,
	onAuthSuccess,
	buttonText,
	loginTermsText,
	loginToggleText,
	signupTermsText,
	signupToggleText,
	partnerTerms,
	termsPrefix,
	termsSuffix,
	showMarketingOptIn = true,
	customEventProps,
	source,
	hideFacebookSignup = false,
	hideGoogleSignup = false,
	hideAppleSignup = false,
	hideAuthModeToggle = false,
	emailSignupCTA,
	loginHint,
	modeTogglePosition = 'above',
	subheadingPosition = 'below',
}: AuthFormProps) => {
	const { isLoading } = useAuth();
	const recaptchaVisible = useRecaptchaVisibleState();
	const partner = usePartnerState();

	const [marketingOptIn, setMarketingOptIn] = useState(true);
	const { authPrompt } = useAuthPrompt();

	const getModeToggle = () => {
		const _hideAuthModeToggle =
			typeof hideAuthModeToggle === 'function' ? hideAuthModeToggle() : hideAuthModeToggle;
		if (_hideAuthModeToggle) return null;
		return <ModeToggle loginToggleText={loginToggleText} signupToggleText={signupToggleText} />;
	};

	const getSubheading = () => {
		if (!subheading) return null;
		return (
			<Text noMargin el="p" data-testid="configurable-auth-form-subheading">
				{subheading}
			</Text>
		);
	};

	if (isLoading) {
		return <Loader color="gray8" />;
	}

	return (
		<FormStateContextProvider>
			<Wrapper>
				<HeadingWrapper>
					{heading && (
						<Text noMargin el="h1" weight={FontWeights.Medium} size={FontSizes['2xl']}>
							{heading}
						</Text>
					)}
					{subheadingPosition === 'above' && getSubheading()}
					{modeTogglePosition === 'above' && getModeToggle()}
					{subheadingPosition === 'below' && getSubheading()}
				</HeadingWrapper>
				<>
					<SocialSignupWrapper>
						<li>
							<EmailAuthButton
								contentAlignment="center"
								marketingOptIn={marketingOptIn}
								onAuthSuccess={onAuthSuccess}
								buttonCTA={emailSignupCTA}
								loginHint={loginHint}
							/>
						</li>
						{!hideFacebookSignup && (
							<li>
								<FacebookAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
						{!hideAppleSignup && (
							<li>
								<AppleAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
						{!hideGoogleSignup && (
							<li>
								<GoogleAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
					</SocialSignupWrapper>
					{modeTogglePosition === 'below' && <FootingWrapper>{getModeToggle()}</FootingWrapper>}
					{!recaptchaVisible && (
						<AuthTerms
							loginTermsText={loginTermsText}
							signupTermsText={signupTermsText as string}
							prefix={termsPrefix}
							suffix={termsSuffix}
						/>
					)}
					{authPrompt === 'signup' && showMarketingOptIn && (
						<StyledMarketingOptIn
							onChange={value => setMarketingOptIn(value)}
							alignment={CheckboxAlign.Left}
							verticalAlignment={CheckboxVerticalAlign.Top}
							textSize={FontSizes.sm}
						/>
					)}
					{partner && partnerTerms && <PartnerSpecificAuthTerms terms={partnerTerms} />}
				</>
			</Wrapper>
		</FormStateContextProvider>
	);
};

export default AuthForm;
